import { forwardRef } from 'react';
import { cn } from '../utils/cn';
import { Icon, type IconName } from '../primitives/Icon';
import { EmptyState } from './EmptyState';
import { formatDate, formatTimestamp } from '../utils/format';

export type ApprovalAction =
  'Submitted' | 'Reviewed' | 'Returned' | 'Approved' | 'Rejected' | 'Published';

export interface ApprovalEntry {
  id?: string;
  stage: string;
  actorName?: string | null;
  actorRole?: string | null;
  action: ApprovalAction;
  occurredAt: string;
  comments?: string | null;
  effectiveDate?: string | null;
}

const actionMeta: Record<
  ApprovalAction,
  { icon: IconName; dot: string; text: string; label: string }
> = {
  Submitted: { icon: 'upload', dot: 'bg-action', text: 'text-action', label: 'Submitted' },
  Reviewed: { icon: 'eye', dot: 'bg-teal', text: 'text-teal', label: 'Reviewed' },
  Returned: {
    icon: 'alert',
    dot: 'bg-attention',
    text: 'text-attention',
    label: 'Returned for changes',
  },
  Approved: { icon: 'check-circle', dot: 'bg-success', text: 'text-success', label: 'Approved' },
  Rejected: { icon: 'x-circle', dot: 'bg-danger', text: 'text-danger', label: 'Rejected' },
  Published: { icon: 'check', dot: 'bg-navy', text: 'text-content', label: 'Published' },
};

export interface ApprovalTimelineProps extends React.HTMLAttributes<HTMLDivElement> {
  entries: ApprovalEntry[];
  /** The stage the item is waiting on, appended as an open step. */
  pendingStage?: string | null;
  pendingWith?: string | null;
  density?: 'comfortable' | 'compact';
}

export const ApprovalTimeline = forwardRef<HTMLDivElement, ApprovalTimelineProps>(
  function ApprovalTimeline(
    { entries, pendingStage, pendingWith, density = 'comfortable', className, ...rest },
    ref,
  ) {
    if (entries.length === 0 && !pendingStage) {
      return (
        <div ref={ref} className={className} {...rest}>
          <EmptyState
            size="sm"
            icon="check-circle"
            title="No approval activity"
            reason="This record has not entered a governance workflow yet."
          />
        </div>
      );
    }

    return (
      <div ref={ref} className={cn('flex flex-col', className)} {...rest}>
        <ol className="flex flex-col">
          {entries.map((entry, index) => {
            const meta = actionMeta[entry.action];
            const isLast = index === entries.length - 1 && !pendingStage;
            return (
              <li key={entry.id ?? `${entry.stage}-${entry.occurredAt}`} className="flex gap-3">
                <div className="flex flex-col items-center">
                  <span
                    aria-hidden="true"
                    className={cn(
                      'flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-white',
                      meta.dot,
                    )}
                  >
                    <Icon name={meta.icon} size={13} strokeWidth={2.25} />
                  </span>
                  {!isLast ? <span className="w-px flex-1 bg-[var(--color-border)]" /> : null}
                </div>
                <div
                  className={cn('flex flex-col gap-0.5', density === 'compact' ? 'pb-3' : 'pb-4')}
                >
                  <p className="text-dense-lg">
                    <span className={cn('font-semibold', meta.text)}>{meta.label}</span>
                    <span className="text-content-secondary"> · {entry.stage}</span>
                  </p>
                  <p className="text-dense text-content-secondary">
                    {entry.actorName ?? 'System'}
                    {entry.actorRole ? ` (${entry.actorRole})` : ''} ·{' '}
                    {formatTimestamp(entry.occurredAt)}
                    {entry.effectiveDate ? ` · effective ${formatDate(entry.effectiveDate)}` : ''}
                  </p>
                  {entry.comments ? (
                    <p className="mt-1 rounded-md border border-line bg-surface-sunken px-2.5 py-1.5 text-dense text-content-secondary">
                      {entry.comments}
                    </p>
                  ) : null}
                </div>
              </li>
            );
          })}

          {pendingStage ? (
            <li className="flex gap-3">
              <span
                aria-hidden="true"
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 border-dashed border-line-strong text-content-muted"
              >
                <Icon name="clock" size={12} />
              </span>
              <div className="flex flex-col gap-0.5">
                <p className="text-dense-lg font-semibold text-content-secondary">
                  Awaiting {pendingStage}
                </p>
                {pendingWith ? (
                  <p className="text-dense text-content-muted">With {pendingWith}</p>
                ) : null}
              </div>
            </li>
          ) : null}
        </ol>
      </div>
    );
  },
);
